// src/components/DiscoveryEngine.jsx
//
// FIXES (May 2026):
//
//  FIX 1 — Geolocation Permissions Policy violation crash:
//    Vercel's Permissions-Policy header had geolocation=() which blocks
//    the API entirely. navigator.geolocation EXISTS as an object but calling
//    getCurrentPosition() throws a Permissions Policy violation before any
//    callback fires. The old code had no try/catch and no error callback.
//
//    Fix A: wrap the entire geolocation call in try/catch to catch the
//           Permissions Policy throw (which is a synchronous-style error).
//    Fix B: add the error callback (2nd arg to getCurrentPosition) to handle
//           PERMISSION_DENIED (code 1) and POSITION_UNAVAILABLE (code 2).
//    Fix C: fall back silently to Lagos default — no crash, no red console error.
//    Fix D: vercel.json updated separately — geolocation=(self) instead of ().
//
//  All original swipe, map, subscription gate, and booking logic preserved.

import React, { useState, useEffect } from 'react';
import { collection, query, limit, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { useSubscription, formatNaira } from '../hooks/useSubscription';
import SubscriptionPage from './SubscriptionPage';

const GOLD         = '#B8860B';
const NAVY         = '#001F3F';
const MAPS_API_KEY = 'AIzaSyB7tcsVKlfo3Ys5BoUrZV9_4XKWNJP_H10';

// Default centre — Lagos Island. Used when geolocation is blocked or denied.
const LAGOS_DEFAULT = { lat: 6.5244, lng: 3.3792 };

const mapStyles = [
  { elementType: 'geometry',           stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill',   stylers: [{ color: '#746855' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'poi',   stylers: [{ visibility: 'off' }] },
];

export default function DiscoveryEngine() {
  const [view,           setView]           = useState('swipe');
  const [stylesFeed,     setStylesFeed]     = useState([]);
  const [stylists,       setStylists]       = useState([]);
  const [currentIndex,   setCurrentIndex]   = useState(0);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [userLocation,   setUserLocation]   = useState(LAGOS_DEFAULT);
  const [showSubModal,   setShowSubModal]   = useState(false);

  const navigate = useNavigate();
  const sub      = useSubscription();

  const { isLoaded } = useJsApiLoader({
    id:               'google-map-script',
    googleMapsApiKey: MAPS_API_KEY,
  });

  useEffect(() => {
    // ── FIX 1: Geolocation with full error handling ───────────────────────────
    //
    //  Three layers of protection:
    //    1. try/catch — catches Permissions Policy violation (thrown before
    //       any callback fires when the browser header blocks the API).
    //    2. Error callback — catches PERMISSION_DENIED and POSITION_UNAVAILABLE
    //       which fire asynchronously after the user denies or GPS fails.
    //    3. Silent fallback — Lagos default used in all failure cases so the
    //       map still renders correctly with no red console errors.
    //
    const requestLocation = () => {
      try {
        navigator.geolocation.getCurrentPosition(
          // Success callback
          (pos) => {
            setUserLocation({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            });
          },
          // FIX 1B: Error callback — handles PERMISSION_DENIED, UNAVAILABLE, TIMEOUT
          (err) => {
            // Silently fall back to Lagos — no console.error, no crash
            // err.code: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
            console.warn(`[Geolocation] Falling back to Lagos default (code ${err.code})`);
            setUserLocation(LAGOS_DEFAULT);
          },
          // Options: short timeout so we don't block the map render
          { timeout: 8000, maximumAge: 60000 }
        );
      } catch (err) {
        // FIX 1A: Permissions Policy violation is caught here (synchronous throw)
        // This fires when vercel.json has geolocation=() blocking the API entirely.
        // After deploying the updated vercel.json (geolocation=(self)), this
        // catch block will no longer be needed — but it's safe to keep as a guard.
        console.warn('[Geolocation] Blocked by Permissions Policy — using Lagos default');
        setUserLocation(LAGOS_DEFAULT);
      }
    };

    // Only attempt geolocation if the API exists in this browser
    if (navigator?.geolocation) {
      requestLocation();
    }
    // If navigator.geolocation doesn't exist (very old browsers), LAGOS_DEFAULT
    // is already the initial state — no action needed.

    // ── Fetch style feed and active stylists ──────────────────────────────────
    const fetchData = async () => {
      try {
        const qStyles = query(
          collection(db, 'global_gallery'),
          orderBy('isPromoted', 'desc'),
          orderBy('createdAt', 'desc'),
          limit(30)
        );
        const styleSnap = await getDocs(qStyles);
        setStylesFeed(styleSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // BOOKING FILTER: only show active stylists on the map
        const qBarbers = query(
          collection(db, 'barbers'),
          where('status', '==', 'active')
        );
        const barberSnap = await getDocs(qBarbers);
        setStylists(
          barberSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(b => b.coordinates) // only stylists who have set their location
        );
      } catch (err) {
        console.error('Discovery Engine data error:', err);
      }
    };

    fetchData();
  }, []);

  const handleSwipe = () => setCurrentIndex(prev => prev + 1);

  const handleTryOn = (styleId) => {
    if (!sub.canStyleTryOn.allowed) { setShowSubModal(true); return; }
    navigate(`/viewer/${styleId}`);
  };

  // ── End of feed ──────────────────────────────────────────────────────────────
  if (currentIndex >= stylesFeed.length && view === 'swipe' && stylesFeed.length > 0) {
    return (
      <div style={centered}>
        <h2 style={{ color: GOLD, letterSpacing: '2px' }}>END OF THE RUNWAY</h2>
        <button onClick={() => setCurrentIndex(0)} style={refreshBtn}>
          RESTART DISCOVERY
        </button>
        <button
          onClick={() => setView('map')}
          style={{ ...refreshBtn, background: 'transparent', border: `2px solid ${GOLD}`, color: GOLD, marginTop: '15px' }}>
          SWITCH TO MAP
        </button>
      </div>
    );
  }

  const currentItem = stylesFeed[currentIndex];

  return (
    <div style={container}>
      {showSubModal && (
        <SubscriptionPage
          paywallMode={true}
          paywallReason="style_exhausted"
          onClose={() => setShowSubModal(false)}
          redirectAfter="/discovery"
        />
      )}

      {/* HEADER */}
      <header style={header}>
        <div style={toggleContainer}>
          <button onClick={() => setView('swipe')} style={view === 'swipe' ? activeToggle : inactiveToggle}>
            RUNWAY
          </button>
          <button onClick={() => setView('map')} style={view === 'map' ? activeToggle : inactiveToggle}>
            MAP VIEW
          </button>
        </div>
      </header>

      <div style={contentArea}>

        {/* ── SWIPE VIEW ── */}
        {view === 'swipe' ? (
          <div style={cardWrapper}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentItem?.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, x: -200 }}
                style={card}
              >
                {currentItem?.isPromoted && <div style={promoRibbon}>DEAL OF THE WEEK</div>}
                <img src={currentItem?.imageUrl} style={mainImg} alt={currentItem?.name || 'Style'} />
                <div style={infoOverlay}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '900' }}>
                        {currentItem?.name?.toUpperCase()}
                      </h3>
                      <p style={{ color: GOLD, fontSize: '0.9rem', margin: '5px 0' }}>
                        by {currentItem?.creatorName || currentItem?.barberName || 'Esdras Stylist'}
                      </p>
                    </div>
                    <div style={priceTag}>
                      {currentItem?.price ? formatNaira(currentItem.price) : 'VIEW'}
                    </div>
                  </div>

                  <div style={actionRow}>
                    <button onClick={handleSwipe} style={skipBtn}>Skip</button>
                    <button
                      onClick={() => handleTryOn(currentItem?.id)}
                      style={{ ...skipBtn, color: GOLD, border: `1px solid ${GOLD}` }}>
                      TRY ON {!sub.canStyleTryOn.allowed && '🔒'}
                    </button>
                    <button
                      onClick={() => navigate(`/barber/${currentItem?.creatorId || currentItem?.barberId}`)}
                      style={bookBtn}>
                      Book Stylist
                    </button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Subscription strip */}
            {!sub.loading && !sub.isSuperAdmin && !sub.isSubscriptionActive && (
              <div style={subStrip}>
                <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>
                  FREE · {sub.lifetimeStylesUsed}/1 try-on used ·{' '}
                </span>
                <span
                  onClick={() => setShowSubModal(true)}
                  style={{ color: GOLD, fontSize: '0.65rem', fontWeight: '700', cursor: 'pointer' }}>
                  UPGRADE FROM ₦3,000/mo →
                </span>
              </div>
            )}
          </div>

        ) : (

          /* ── MAP VIEW ── */
          <div style={mapWrapper}>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={userLocation}
                zoom={12}
                options={{ styles: mapStyles, disableDefaultUI: true }}
              >
                {stylists.map(barber => (
                  <Marker
                    key={barber.id}
                    position={{ lat: barber.coordinates.lat, lng: barber.coordinates.lng }}
                    onClick={() => setSelectedBarber(barber)}
                    icon={{
                      path:         'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
                      fillColor:    GOLD,
                      fillOpacity:  1,
                      strokeWeight: 1,
                      strokeColor:  '#000',
                      scale:        1.5,
                    }}
                  />
                ))}

                {selectedBarber && (
                  <InfoWindow
                    position={{ lat: selectedBarber.coordinates.lat, lng: selectedBarber.coordinates.lng }}
                    onCloseClick={() => setSelectedBarber(null)}
                  >
                    <div style={infoWindowStyle}>
                      <h4 style={{ margin: '0 0 5px', color: NAVY, fontWeight: 'bold' }}>
                        {selectedBarber.displayName || selectedBarber.businessName}
                      </h4>
                      <p style={{ fontSize: '0.7rem', color: '#666', margin: '0 0 10px' }}>
                        {selectedBarber.location || selectedBarber.address || ''}
                      </p>
                      <button
                        onClick={() => navigate(`/barber/${selectedBarber.id}`)}
                        style={miniBookBtn}>
                        VIEW PROFILE
                      </button>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <div style={centered}>
                <p style={{ color: GOLD, letterSpacing: '3px', fontSize: '0.75rem' }}>
                  CONNECTING TO ESDRAS GRID...
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────────────────────
const container       = { height:'100vh', background:NAVY, color:'white', display:'flex', flexDirection:'column', overflow:'hidden' };
const header          = { padding:'20px', display:'flex', justifyContent:'center', background:'rgba(0,0,0,0.3)', zIndex:10 };
const toggleContainer = { display:'flex', background:'rgba(255,255,255,0.05)', borderRadius:'50px', padding:'5px', border:'1px solid rgba(255,255,255,0.1)' };
const activeToggle    = { background:GOLD, color:'black', border:'none', padding:'10px 20px', borderRadius:'50px', fontWeight:'bold', cursor:'pointer', transition:'0.3s' };
const inactiveToggle  = { background:'transparent', color:'white', border:'none', padding:'10px 20px', borderRadius:'50px', cursor:'pointer', transition:'0.3s' };
const contentArea     = { flex:1, position:'relative', overflow:'hidden' };
const cardWrapper     = { height:'100%', display:'grid', placeItems:'center', padding:'20px' };
const mapWrapper      = { height:'100%', width:'100%' };
const card            = { width:'100%', maxWidth:'400px', height:'75vh', background:'#000', borderRadius:'30px', overflow:'hidden', position:'relative', border:`1px solid ${GOLD}`, boxShadow:'0 20px 50px rgba(0,0,0,0.5)' };
const mainImg         = { width:'100%', height:'100%', objectFit:'cover', display:'block' };
const promoRibbon     = { position:'absolute', top:'20px', right:'-35px', background:'#D22B2B', color:'white', padding:'10px 40px', transform:'rotate(45deg)', fontSize:'0.7rem', fontWeight:'bold', zIndex:5, boxShadow:'0 2px 10px rgba(0,0,0,0.3)' };
const infoOverlay     = { position:'absolute', bottom:0, width:'100%', padding:'30px 20px', background:'linear-gradient(transparent,rgba(0,0,0,1))', boxSizing:'border-box' };
const priceTag        = { background:GOLD, color:'black', padding:'5px 12px', borderRadius:'8px', fontWeight:'800', fontSize:'1rem', whiteSpace:'nowrap' };
const actionRow       = { display:'flex', gap:'10px', marginTop:'20px' };
const bookBtn         = { flex:2, background:GOLD, border:'none', padding:'15px', borderRadius:'12px', fontWeight:'bold', cursor:'pointer', fontSize:'0.9rem' };
const skipBtn         = { flex:1, background:'rgba(255,255,255,0.1)', color:'white', border:'none', borderRadius:'12px', cursor:'pointer', fontWeight:'bold' };
const centered        = { height:'100vh', background:NAVY, display:'grid', placeItems:'center', textAlign:'center' };
const refreshBtn      = { background:GOLD, border:'none', padding:'15px 30px', borderRadius:'50px', fontWeight:'bold', cursor:'pointer' };
const infoWindowStyle = { padding:'10px', minWidth:'150px' };
const miniBookBtn     = { width:'100%', marginTop:'10px', background:GOLD, border:'none', color:'black', padding:'8px', borderRadius:'5px', fontWeight:'bold', cursor:'pointer', fontSize:'0.75rem' };
const subStrip        = { marginTop:'16px', textAlign:'center' };
